// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 41-groups-management.md

import * as React from 'react';
import { Building2, ChevronDown, ChevronRight, Users } from 'lucide-react';
import type { AppAccessRole, AppGroupBinding } from '@greenlight/shared';

import { AD_ROW } from '@/components/greenlight/surfaces';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

import { members } from './AccessSearch';

const SOURCE_LABEL = { greenlight: 'Greenlight group', idp: 'Directory group' } as const;

/**
 * A group on the access list: what it is, how many people it admits, and — on
 * expansion — which people those are. The membership read is scoped to groups
 * bound to this app (docs/41 §RBAC and audit), so it is only ever offered here.
 */
export function AccessGroupRow({
  appId,
  binding,
  onRoleChange,
}: {
  appId: string;
  binding: AppGroupBinding;
  onRoleChange: (next: AppAccessRole | 'remove') => void;
}): React.ReactElement {
  const [expanded, setExpanded] = React.useState(false);
  const Icon = binding.source === 'idp' ? Building2 : Users;

  return (
    <div data-testid={`access-row-group-${binding.role}`}>
      <div className={cn(AD_ROW, 'justify-between')}>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-label={`Members of ${binding.name}`}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
          data-testid={`access-group-toggle-${binding.id}`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold leading-snug">
              {binding.name}
            </span>
            <span className="block truncate text-[12px] text-muted-foreground">
              {SOURCE_LABEL[binding.source]} · {members(binding.member_count)}
            </span>
          </span>
        </button>
        <Select
          value={binding.role}
          onValueChange={(v) => onRoleChange(v as AppAccessRole | 'remove')}
        >
          <SelectTrigger className="h-9 w-36 shrink-0" aria-label={`Role for ${binding.name}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="viewer">Viewer</SelectItem>
            <SelectItem value="editor">Editor</SelectItem>
            <SelectItem value="remove">Remove access</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {expanded && <GroupMembers appId={appId} binding={binding} />}
    </div>
  );
}

function GroupMembers({
  appId,
  binding,
}: {
  appId: string;
  binding: AppGroupBinding;
}): React.ReactElement {
  const membersKey = ['group-members', appId, binding.id] as const;
  const query = api.rbac.listGroupMembers.useQuery(
    membersKey,
    {
      params: { id: appId },
      query:
        binding.source === 'idp'
          ? { idp_group_registration_id: binding.idp_group_registration_id ?? '' }
          : { group_id: binding.group_id ?? '' },
    },
    { queryKey: membersKey, staleTime: 30_000 },
  );

  const body = query.data?.status === 200 ? query.data.body : undefined;

  return (
    <div
      className="border-t border-border/50 bg-muted/30 px-[18px] py-3"
      data-testid={`access-group-members-${binding.id}`}
    >
      {query.isLoading && <p className="text-[12px] text-muted-foreground">Loading members…</p>}
      {query.isError && (
        <p className="text-[12px] text-destructive" role="alert">
          Couldn&rsquo;t load who this group admits.
        </p>
      )}
      {body && (
        <>
          {body.items.length === 0 ? (
            <p className="text-[12px] text-muted-foreground">
              This group has no members, so it admits nobody extra.
            </p>
          ) : (
            <ul className="space-y-1">
              {body.items.map((m) => (
                <li key={m.user_id} className="truncate text-[12px]">
                  <span className="font-medium">{m.display_name ?? m.email}</span>
                  {m.display_name && <span className="text-muted-foreground"> · {m.email}</span>}
                  {!m.is_active && <span className="text-muted-foreground"> · deactivated</span>}
                </li>
              ))}
            </ul>
          )}
          {body.next_cursor && (
            <p className="mt-2 text-[12px] text-muted-foreground">
              Showing the first {body.items.length}. The full membership is managed under Users
              &amp; RBAC.
            </p>
          )}
          {body.coverage === 'signed_in' && (
            <p className="mt-2 text-[12px] text-muted-foreground" data-testid="access-idp-caveat">
              Directory memberships are what Greenlight recorded when these people last signed in,
              not a live read of your directory.
            </p>
          )}
        </>
      )}
    </div>
  );
}
